import { useState, useEffect, useRef, useCallback } from "react";
import { productService } from "../services/product.service";
import { customerService } from "../services/customer.service";

export interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  category: "product" | "customer";
  url: string;
}

export function useGlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const [productsRes, customersRes] = await Promise.allSettled([
        productService.getProducts({ search: q, limit: 5, page: 1 }),
        customerService.getCustomers({ search: q, limit: 5, page: 1 }),
      ]);

      const mapped: SearchResult[] = [];

      if (productsRes.status === "fulfilled") {
        const products = productsRes.value?.data || productsRes.value || [];
        (Array.isArray(products) ? products : [])
          .slice(0, 5)
          .forEach((p: any) => {
            mapped.push({
              id: p.id,
              title: p.name || p.tradeName || "Unnamed Product",
              subtitle: p.genericName
                ? `${p.genericName} • ${p.dosageForm || ""}`
                : p.category?.name || "Product",
              category: "product",
              url: `/products/${p.id}`,
            });
          });
      }

      if (customersRes.status === "fulfilled") {
        const customers = customersRes.value?.data || customersRes.value || [];
        (Array.isArray(customers) ? customers : [])
          .slice(0, 5)
          .forEach((c: any) => {
            mapped.push({
              id: c.id,
              title: c.name || "Unnamed Customer",
              subtitle: c.phone || c.email || "Customer",
              category: "customer",
              url: `/customers/${c.id}`,
            });
          });
      }

      setResults(mapped);
    } catch {
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
    debounceRef.current = setTimeout(() => search(query), 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
  }, []);

  return {
    query,
    setQuery,
    results,
    isSearching,
    isOpen,
    setIsOpen,
    close,
  };
}
